// src/features/shared-scope/hooks/useSharedLinksActivity.ts
// ────────────────────────────────────────────────────────────────────────────
// Shared Links Activity Log — Hook-Owns-All-State
//
// Owns: fetch, cache, mutations, derived state, error handling.
// Components are pure renderers — zero data logic in JSX.
// ────────────────────────────────────────────────────────────────────────────
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchSharedLinksActivity,
  revokeSharedLink,
  recreateSharedLink,
  type ActivityLogEntry,
  type ActivityLogCounters,
  type SharedLinkStatus,
} from '../api/sharedScopeApi';

// ── Query Keys ───────────────────────────────────────────────────────────

const KEYS = {
  activity: ['shared-links', 'activity'] as const,
};

// ── Hook ─────────────────────────────────────────────────────────────────

export function useSharedLinksActivity() {
  const queryClient = useQueryClient();

  // ── UI state ─────────────────────────────────────────────────────────
  const [selectedToken, setSelectedToken] = useState<string>('');
  const [searchText, setSearchText]       = useState('');
  const [statusFilter, setStatusFilter]   = useState<SharedLinkStatus | 'all'>('all');

  // Force re-render every second for live timeAgo
  const [, bumpTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => bumpTick(t => t + 1), 1_000);
    return () => window.clearInterval(id);
  }, []);

  // ── Query ────────────────────────────────────────────────────────────
  const {
    data,
    isLoading,
    error,
  } = useQuery({
    queryKey: KEYS.activity,
    queryFn:  fetchSharedLinksActivity,
    refetchInterval: 15_000,
    staleTime: 10_000,
  });

  const items:    ActivityLogEntry[]  = data?.items    ?? [];
  const counters: ActivityLogCounters = data?.counters ?? { active: 0, expired: 0, revoked: 0 };

  // ── Filter + search ──────────────────────────────────────────────────
  const filteredItems = useMemo(() => {
    let result = items;

    if (statusFilter !== 'all') {
      result = result.filter(e => e.status === statusFilter);
    }

    const q = searchText.trim().toLowerCase();
    if (q) {
      result = result.filter(e =>
        (e.label ?? '').toLowerCase().includes(q)
        || e.scope_type.toLowerCase().includes(q)
        || e.scope_ns.toLowerCase().includes(q)
        || (e.scope_key ?? '').toLowerCase().includes(q)
        || e.created_by.toLowerCase().includes(q)
        || String(e.tenant_id).includes(q),
      );
    }

    return result;
  }, [items, statusFilter, searchText]);

  // ── Selected entry ───────────────────────────────────────────────────
  const selectedEntry = useMemo(
    () => filteredItems.find(e => e.token === selectedToken) ?? null,
    [filteredItems, selectedToken],
  );

  // Auto-select first item if none selected
  useEffect(() => {
    if (selectedToken && filteredItems.some(e => e.token === selectedToken)) return;
    if (filteredItems.length > 0) setSelectedToken(filteredItems[0].token);
  }, [filteredItems, selectedToken]);

  // ── Mutations ────────────────────────────────────────────────────────
  const revokeMutation = useMutation({
    mutationFn: (token: string) => revokeSharedLink(token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.activity });
    },
  });

  const recreateMutation = useMutation({
    mutationFn: (entry: ActivityLogEntry) => recreateSharedLink(entry),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.activity });
    },
  });

  const handleRevoke   = useCallback((token: string) => revokeMutation.mutate(token), [revokeMutation]);
  const handleRecreate = useCallback((entry: ActivityLogEntry) => recreateMutation.mutate(entry), [recreateMutation]);

  return {
    // Data
    items: filteredItems,
    counters,
    selectedEntry,
    isLoading,
    error,

    // UI state
    selectedToken,
    setSelectedToken,
    searchText,
    setSearchText,
    statusFilter,
    setStatusFilter,

    // Mutations
    handleRevoke,
    handleRecreate,
    isRevoking:   revokeMutation.isPending,
    isRecreating: recreateMutation.isPending,
    recreatedUrl: (recreateMutation.data as any)?.url ?? null,
  };
}
